import {
  BoundSelectField,
  BoundTextField,
  Button,
  FormLines,
  ModalBody,
  ModalFooter,
  ModalHeader,
  useModal,
} from "@homebound/beam";
import { Observer } from "mobx-react";
import { useHistory } from "react-router";
import { createInvoiceTermUrl } from "src/RouteUrls";
import { LotType, SaveInvoiceScheduleTemplateInput } from "src/generated/graphqlTypes";
import {
  CreateInvoiceTerms_CustomerFragment,
  CreateInvoiceTerms_RegionFragment,
  useCreateInvoiceTermsMetadataQuery,
} from "src/routes/billing/invoice-terms/components/CreateInvoiceTermsModal.generated";
import { queryResult } from "src/utils";
import { ObjectConfig, required, useFormState } from "src/utils/formState";

export function CreateInvoiceTermsModal() {
  const query = useCreateInvoiceTermsMetadataQuery({ fetchPolicy: "cache-first" });
  return queryResult(query, (data) => <CreateInvoiceTermsContent customers={data.customers} regions={data.regions} />);
}

type CreateInvoiceFormProps = {
  customers: CreateInvoiceTerms_CustomerFragment[];
  regions: CreateInvoiceTerms_RegionFragment[];
};

function CreateInvoiceTermsContent({ customers, regions }: CreateInvoiceFormProps) {
  const formState = useFormState({
    config: formConfig,
    init: { onlyOnce: true, input: { lotType: LotType.Bool } },
    addRules(state) {
      // For bool lot type, customer is required
      state.customerId.rules.push(() =>
        state.lotType.value === LotType.Bool && !state.customerId.value ? "Required" : undefined,
      );
    },
  });
  const history = useHistory();
  const { closeModal } = useModal();

  return (
    <Observer>
      {() => (
        <>
          <ModalHeader>Create Invoice Terms</ModalHeader>
          <ModalBody>
            <FormLines>
              <>
                <BoundTextField field={formState.name} />
                <BoundSelectField
                  field={formState.lotType}
                  options={Object.values(LotType).map((type) => ({ id: type, name: type }))}
                />
                {formState.lotType.value === LotType.Bool ? (
                  <BoundSelectField
                    field={formState.customerId}
                    options={customers.map((customer) => ({ id: customer.id, name: customer.name }))}
                    disabledOptions={customers.filter((customer) => customer.isHti).map((c) => c.id)}
                  />
                ) : (
                  <BoundSelectField
                    field={formState.regionId}
                    unsetLabel="None"
                    options={regions.map((region) => ({ id: region.id, name: region.name }))}
                  />
                )}
              </>
            </FormLines>
          </ModalBody>
          <ModalFooter>
            <Button variant="quaternary" label="Close" onClick={closeModal} />
            <Button
              disabled={!formState.valid}
              label={"Create Invoice Terms"}
              onClick={() => {
                closeModal();
                history.push({ pathname: createInvoiceTermUrl(), state: formState.value });
              }}
            />
          </ModalFooter>
        </>
      )}
    </Observer>
  );
}

const formConfig: ObjectConfig<SaveInvoiceScheduleTemplateInput> = {
  name: { type: "value", rules: [required] },
  lotType: { type: "value", rules: [required] },
  customerId: { type: "value" },
  regionId: { type: "value" },
};
